import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from 'components/ui/carousel';
import { useSocialMediaContext } from 'features/social-media/SocialMediaContext';
import { ArrowLeft, ArrowRight, ChevronUp, Check, Sparkle, SpellCheck } from 'lucide-react';
import AutoHeight from 'embla-carousel-auto-height';
import ReactQuill from 'react-quill';
import React, { useState, useEffect } from 'react';
import TurndownService from 'turndown';
import { useSearchParams } from 'react-router-dom';
import { ISocialMediaContext } from '../../types';
import { Button } from 'components/ui/button';
import SavePublishDialog from './SavePublishDialog';
import { toast } from 'react-hot-toast';
import EditToolbar from 'features/email-generator/components/Email/EditToolbar';
import EditPanel from './EditPanel';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import AuthRequiredModal from './AuthRequiredModal';
import SaveRequiredModal from './SaveRequiredModal';

import 'react-quill/dist/quill.snow.css';
import { useFormOptions } from 'features/social-media/hooks/useFormOptions';
import PremiumBanner from './PremiumBanner';
import SaveDialog from './SaveDialog';

export default function ResponseSection() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);
  const [searchParams] = useSearchParams();
  const [contentLength, setContentLength] = useState(0);
  const [localContent, setLocalContent] = useState<ISocialMediaContext[] | null>(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<'Edit' | 'Format'>('Edit');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalType, setAuthModalType] = useState<'save' | 'export'>('save');
  const [saveRequiredOpen, setSaveRequiredOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const { 
    isEditing, 
    setIsEditing, 
    socialMediaContent, 
    updateSocialMediaContent,
  } = useSocialMediaContext();

  const postId = socialMediaContent?.[0]?.postId;
  const isSaved = Boolean(postId);

  const { data } = useFormOptions();

  const myColors = [
    'purple',
    '#785412',
    '#452632',
    '#856325',
    '#963254',
    '#254563',
    'white',
  ];
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ align: ['right', 'center', 'justify'] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      [{ color: myColors }],
      [{ background: myColors }],
    ],
  };

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'link',
    'color',
    'image',
    'background',
    'align',
  ];

  const [code, setCode] = React.useState('');

  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
    emDelimiter: '_',
  });

  const handleProcedureContentChange = (content: string) => {
    try {
      const markdownContent = turndownService.turndown(content);
      setCode(content);
      setContentLength(markdownContent.length);

      if (socialMediaContent && api) {
        socialMediaContent[api.selectedScrollSnap()].markdownContent =
          markdownContent;
        socialMediaContent[api.selectedScrollSnap()].content = content;
      }
    } catch (error) {
      console.error('Error converting to markdown:', error);
      setCode(content);
      setContentLength(content.length);
    }
  };

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    setCode(
      socialMediaContent && api
        ? socialMediaContent[api.selectedScrollSnap()]?.content
        : ''
    );

    setContentLength(
      socialMediaContent && api
        ? socialMediaContent[api.selectedScrollSnap()]?.content.length
        : 0
    );

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
      setCode(
        socialMediaContent && api
          ? socialMediaContent[api.selectedScrollSnap()]?.content
          : ''
      );
    });
  }, [api, socialMediaContent]);

  // Try to get content from localStorage on mount
  useEffect(() => {
    console.log("socialmediacconmount", socialMediaContent);
    if (!socialMediaContent?.length) {  // Check if array is empty
      const savedContent = localStorage.getItem('socialMediaContent');
      if (savedContent) {
        try {
          const parsedContent = JSON.parse(savedContent) as ISocialMediaContext[];
          setLocalContent(parsedContent);
          updateSocialMediaContent(parsedContent);
          localStorage.removeItem('socialMediaContent'); // Clear after restoring
        } catch (error) {
          console.error('Error parsing saved content:', error);
        }
      }
    }
  }, [socialMediaContent, updateSocialMediaContent]);

  // Use either context content or local content
  const displayContent = socialMediaContent || localContent;

  const handleImageLoad = () => {
    if (api) {
      api.reInit();
    }
  };

  const handleExport = async () => {
    if (!displayContent?.length) {
      toast.error('No content to export');
      return;
    }

    try {
      // await exportContent(displayContent);
      toast.success('Content exported successfully');
      setShowExportOptions(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export content');
    }
  };

  // Update isMobile based on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle publish button click with authentication and save checks
  const handlePublishClick = () => {
    // First check if user is authenticated
    if (!isAuthenticated) {
      setAuthModalType('save');
      setShowAuthModal(true);
      return;
    }
    
    // Then check if content has a postId (has been saved)
    const hasPostId = socialMediaContent?.[0]?.postId;
    
    if (!hasPostId) {
      setSaveRequiredOpen(true);
    } else {
      setPublishDialogOpen(true);
    }
  };

  if (isEditing) {
    return (
      <section className="px-6 flex-1 h-full py-6">
        <ReactQuill
          defaultValue={
            displayContent && api
              ? displayContent[api.selectedScrollSnap()]?.content
              : ''
          }
          theme="snow"
          modules={modules}
          formats={formats}
          value={code}
          onChange={handleProcedureContentChange}
          className="h-[90%]"
        />
        <div>{contentLength} characters</div>
      </section>
    );
  }

  return (
    <section className="flex-1 flex flex-col h-[calc(100vh-56px)] overflow-hidden relative">
      {searchParams.get('name') === 'x' && <PremiumBanner />}
      {/* Carousel Navigation */}
      <div className="flex items-center justify-center gap-5 py-2 sm:py-4 text-primary">
        <button onClick={() => api?.scrollPrev()}>
          <ArrowLeft size={18} />
        </button>
        <p className="text-xs sm:text-sm">
          {current} / {count}
        </p>
        <button onClick={() => api?.scrollNext()}>
          <ArrowRight size={18} />
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-x-hidden px-2 sm:px-4 lg:px-0">
        <div className="h-full max-w-2xl mx-auto lg:w-1/2">
          <Carousel setApi={setApi} plugins={[AutoHeight()]} className="h-full">
            <CarouselContent className="h-full">
              {displayContent?.map((item, index) => (
                <CarouselItem key={index} className="h-full">
                  <div className="w-full prose prose-sm sm:prose prose-img:rounded-md prose-img:object-cover prose-img:h-full h-full overflow-y-auto pb-24 lg:pb-0">
                    <div
                      className="text-text-primary text-sm sm:text-base lg:text-lg"
                      dangerouslySetInnerHTML={{ __html: item.content }}
                    />
                    {item.image && (
                      <div className="mt-3 sm:mt-4 w-full aspect-video">
                        <img 
                          src={item.image} 
                          className="w-full h-full object-cover rounded-md" 
                          onLoad={handleImageLoad}
                        />
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>

      {/* Mobile Fixed Bottom Buttons */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-background border-t flex gap-2">
        <Button
          variant="customSecondary"
          className="flex-1"
          onClick={() => {
            setIsPanelOpen(true);
            setActivePanel('Edit');
          }}
        >
          Edit
        </Button>
        
        <div className="flex-1 flex gap-1">
          <Button
            variant="customPrimary"
            className="flex-1 rounded-r-none"
            onClick={handlePublishClick}
          >
            Publish
          </Button>
          
          <div className="relative">
            <Button
              variant="customPrimary"
              size="icon"
              className="rounded-l-none"
              onClick={() => setShowExportOptions(!showExportOptions)}
            >
              <ChevronUp className={`transform ${showExportOptions ? 'rotate-180' : ''}`} />
            </Button>

            {/* Dropdown Menu */}
            {showExportOptions && (
              <div className="absolute bottom-full right-0 mb-2 w-32 bg-background rounded-lg shadow-lg border overflow-hidden">
                {!isSaved && (
                  <button
                    className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => {
                      if (!isAuthenticated) {
                        setAuthModalType('save');
                        setShowAuthModal(true);
                      } else {
                        setSaveDialogOpen(true);
                      }
                    }}
                  >
                    Save
                  </button>
                )}
                <button
                  className="w-full px-4 py-2 text-sm text-left hover:bg-gray-100 flex items-center justify-between opacity-50 cursor-not-allowed"
                  disabled
                >
                  Export
                  <span className="text-xs bg-primary/30 text-primary px-1.5 py-0.5 rounded-2xl ml-1">Soon</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Panel */}
      {isPanelOpen && window.innerWidth < 1024 && (
        <>
          {/* Overlay for click-away */}
          <div 
            className="fixed inset-0 bg-black/20 z-[998]" 
            onClick={() => setIsPanelOpen(false)}
          />
          
          <div className="fixed bottom-0 left-0 w-full bg-background pb-5 z-[999] shadow-2xl rounded-t-md">
            <div className="flex w-full">
              {/* Edit Tab */}
              <button
                className={`flex w-1/2 justify-center items-center gap-2 py-5 rounded-t-xl ${
                  activePanel === 'Edit' ? 'bg-primary' : 'bg-secondary'
                }`}
                onClick={() => setActivePanel('Edit')}
              >
                <span
                  className={`font-medium text-[18px] ${
                    activePanel === 'Edit' ? 'text-white' : 'text-primary'
                  }`}
                >
                  Edit
                </span>
                <Sparkle
                  size={20}
                  color={activePanel === 'Edit' ? 'white' : '#846CEC'}
                />
              </button>

              {/* Format Tab */}
              <button
                className={`flex w-1/2 justify-center items-center gap-2 py-5 rounded-t-xl ${
                  activePanel === 'Format' ? 'bg-primary' : 'bg-secondary'
                }`}
                onClick={() => setActivePanel('Format')}
              >
                <span
                  className={`font-medium text-[18px] ${
                    activePanel === 'Format' ? 'text-white' : 'text-primary'
                  }`}
                >
                  Format
                </span>
                <SpellCheck
                  size={20}
                  color={activePanel === 'Format' ? 'white' : '#846CEC'}
                />
              </button>
            </div>

            {/* Panel Content */}
            <div className="p-4">
              {activePanel === 'Edit' ? (
                <div className="h-[calc(100vh-250px)] overflow-y-auto">
                  <EditPanel formOptions={data} />
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <ReactQuill
                    value={code}
                    onChange={handleProcedureContentChange}
                    theme="snow"
                    modules={modules}
                    formats={formats}
                    className="h-[200px]"
                  />
                  <div>{contentLength} characters</div>
              
                </div>
              )}
            </div>

            {/* Done Button */}
            <Button
              variant="customPrimary"
              className="absolute right-3 bottom-5 h-[34px]"
              onClick={() => setIsPanelOpen(false)}
            >
              <Check />
            </Button>
          </div>
        </>
      )}

      {/* Save & Publish Dialog */}
      <SavePublishDialog 
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
      />

      {/* Save Dialog */}
      <SaveDialog
        isSaveDialogOpen={saveDialogOpen}
        setIsSaveDialogOpen={setSaveDialogOpen}
        postTitle={socialMediaContent?.[0]?.postTitle || "Untitled Post"}
        setPostTitle={(title) => {
          if (socialMediaContent && socialMediaContent.length > 0) {
            const updatedContent = [...socialMediaContent];
            updatedContent[0] = { ...updatedContent[0], postTitle: title };
            updateSocialMediaContent(updatedContent);
          }
        }}
        platformName={searchParams.get('name')?.toLowerCase() as 'linkedin' | 'x'}
      />

      {/* Auth Required Modal */}
      <AuthRequiredModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        type={authModalType}
      />

      {/* Save Required Modal */}
      <SaveRequiredModal
        isOpen={saveRequiredOpen}
        onOpenChange={setSaveRequiredOpen}
        postTitle={socialMediaContent?.[0]?.postTitle || "Untitled Post"}
        setPostTitle={(title) => {
          if (socialMediaContent && socialMediaContent.length > 0) {
            const updatedContent = [...socialMediaContent];
            updatedContent[0] = { ...updatedContent[0], postTitle: title };
            updateSocialMediaContent(updatedContent);
          }
        }}
        platformName={searchParams.get('name')?.toLowerCase() as 'linkedin' | 'x'}
      />

      {/* Click-away listener for dropdown */}
      {/* {showExportOptions && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setShowExportOptions(false)}
        />
      )} */}
    </section>
  );
}